import { Component } from '@theme/component'

/**
 * A custom element that displays a tooltip.
 *
 * @typedef {Object} TooltipComponentRefs
 * @property {HTMLElement} tooltipTrigger - The element that triggers the tooltip.
 * @property {HTMLElement} tooltipContent - The content of the tooltip.
 *
 * example usage:
 * ```html
 * <tooltip-component>
 *  <button ref="tooltipTrigger">Hover me</button>
 *  <div ref="tooltipContent" role="tooltip">This is a tooltip</div>
 * </tooltip-component>
 * ```
 *
 * @extends {Component<TooltipComponentRefs>}
 */
class TooltipComponent extends Component {
  requiredRefs = ['tooltipTrigger', 'tooltipContent']
  #uuid = crypto.randomUUID()

  connectedCallback() {
    super.connectedCallback()
    this.refs.tooltipContent.id = this.#uuid
    this.refs.tooltipContent.role = 'tooltip'
    this.refs.tooltipContent.setAttribute('data-state', 'hidden')
    this.refs.tooltipTrigger.setAttribute('aria-describedby', this.#uuid)
    this.refs.tooltipTrigger.addEventListener('mouseenter', this.#showTooltip)
    this.refs.tooltipTrigger.addEventListener('mouseleave', this.#hideTooltip)
    document.addEventListener('keydown', this.#handleKeydown)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.refs.tooltipTrigger.removeEventListener(
      'mouseenter',
      this.#showTooltip
    )
    this.refs.tooltipTrigger.removeEventListener(
      'mouseleave',
      this.#hideTooltip
    )
    document.removeEventListener('keydown', this.#handleKeydown)
  }

  #showTooltip = () => {
    const left = this.refs.tooltipTrigger.offsetWidth / 2
    this.refs.tooltipContent.style.left = `${left}px`
    this.refs.tooltipContent.setAttribute('data-state', 'visible')
  }

  #hideTooltip = () => {
    this.refs.tooltipContent.setAttribute('data-state', 'hidden')
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#hideTooltip()
    }
  }
}

if (!customElements.get('tooltip-component')) {
  customElements.define('tooltip-component', TooltipComponent)
}
